import { useChat } from "@ai-sdk/react";
import { toAISdkMessages } from "@mastra/ai-sdk/ui";
import { getMastraThreadLangfuseSessionId } from "@repo/app-types/entities/researchAgent";
import { checkIsAbortError } from "@repo/ui/helpers/errors";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { useLazyMastraThreadMessagesQuery } from "../../../api/entities/mastraThread.js";
import { getSessionAccessToken, mastraBaseUrl } from "../../../api/shared.js";

export interface MastraChatOptions {
  agentId: string;
  /** Local thread ID. Switching it resets local state and reloads history. */
  localThreadId?: string;
  /** Messages to pre-populate the chat with before any user interaction. */
  initialMessages?: UIMessage[];
  /**
   * Populated with a fn to append a static assistant message without triggering
   * a run (local state only; never persisted). Same caveat as before: for
   * memory-backed runtimes the injected message is not part of agent context.
   */
  appendAssistantMessageRef?: {
    current: ((message: UIMessage) => void) | null;
  };
  /**
   * Mastra thread to load history from. History reloads when `localThreadId`
   * changes; giving an existing local thread a remote counterpart (as happens
   * when the first message creates the thread) does not reload it.
   */
  loadThreadId?: string;
  /** Resolves per-send memory + request context (may create the thread). */
  onSend?: () => Promise<MastraChatSendInfo>;
}

export interface MastraChatSendInfo {
  /** Mastra memory thread. Omit for ephemeral (memory-less) chats. */
  thread?: { resourceId: string; threadId: string };
  /** Request context forwarded to the agent (e.g. playbookId). */
  requestContext?: Record<string, unknown>;
  onFinish?: () => void;
}

export interface MastraChat {
  messages: UIMessage[];
  /** Send a user text message and stream the agent's reply. */
  sendMessage: (text: string) => void;
  /** Abort the in-flight run. */
  stop: () => void;
  /** True while a run is submitting or streaming. */
  isRunning: boolean;
  /** True while loading thread history. */
  isLoading: boolean;
  setMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
}

/**
 * Drives an admin agent chat through AI SDK UI's `useChat` against the backend
 * `chatRoute` (`POST {mastraBaseUrl}/chat/:agentId`). Replaces the
 * assistant-ui `useExternalStoreRuntime` + manual `@mastra/client-js`
 * streaming in `useMastraAgentRuntime`.
 */
export function useMastraChat(options: MastraChatOptions): MastraChat {
  const [defaultLocalThreadId] = useState(() => uuid());
  const { agentId, localThreadId = defaultLocalThreadId } = options;

  // Keep the latest options/derived values in a ref so the memoized transport's
  // `prepareSendMessagesRequest` always reads current values without re-creating
  // the transport (which would reset `useChat`).
  const sendRef = useRef<{
    onSend?: MastraChatOptions["onSend"];
    localThreadId: string;
    agentId: string;
  }>({ onSend: options.onSend, localThreadId, agentId });
  sendRef.current = { onSend: options.onSend, localThreadId, agentId };

  // onFinish from the most recently resolved send, invoked when the run ends.
  const pendingFinishRef = useRef<(() => void) | undefined>(undefined);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${mastraBaseUrl}/chat/${agentId}`,
        prepareSendMessagesRequest: async ({ messages }) => {
          const send = await sendRef.current.onSend?.();
          pendingFinishRef.current = send?.onFinish;

          const memory = send?.thread
            ? { resource: send.thread.resourceId, thread: send.thread.threadId }
            : undefined;

          // With server memory the agent owns history, so only the latest user
          // message is sent; memory-less agents send the full local list.
          const outgoing = memory ? messages.slice(-1) : messages;

          const langfuseSessionId = getLangfuseSessionId({
            agentId: sendRef.current.agentId,
            localThreadId: sendRef.current.localThreadId,
            thread: send?.thread,
          });

          const token = await getSessionAccessToken();
          return {
            headers: { Authorization: `Bearer ${token}` },
            body: {
              messages: outgoing,
              ...(memory ? { memory } : {}),
              requestContext: {
                ...(langfuseSessionId ? { langfuseSessionId } : {}),
                ...send?.requestContext,
              },
            },
          };
        },
      }),
    [agentId],
  );

  // `onError` runs before `setMessages` is assigned below, so reach it via a
  // ref (its identity is stable; the ref is set on every render before any
  // error can fire).
  const setMessagesRef = useRef<MastraChat["setMessages"] | null>(null);

  const { messages, sendMessage, stop, status, setMessages } =
    useChat<UIMessage>({
      id: localThreadId,
      messages: options.initialMessages,
      transport,
      onFinish: () => {
        pendingFinishRef.current?.();
        pendingFinishRef.current = undefined;
      },
      // On a send/stream failure AI SDK only flips `status` to "error"; the
      // chat surfaces don't render that, so (matching the previous runtime)
      // append a visible assistant error message. Aborts (stop) don't reach
      // here. The user's prompt is preserved, so they can retry by resending.
      onError: () => {
        setMessagesRef.current?.((prev) => [
          ...prev,
          {
            id: `error-${uuid()}`,
            role: "assistant",
            parts: [{ type: "text", text: "Something went wrong." }],
          },
        ]);
      },
    });
  setMessagesRef.current = setMessages;

  // Expose an imperative handle to append a static assistant message to local
  // state without triggering a run (e.g. the playbook editor's "Optimize"
  // clarifying prompt).
  const { appendAssistantMessageRef } = options;
  useEffect(() => {
    if (!appendAssistantMessageRef) {
      return;
    }
    appendAssistantMessageRef.current = (message) =>
      setMessages((prev) => [...prev, message]);
    return () => {
      appendAssistantMessageRef.current = null;
    };
  }, [appendAssistantMessageRef, setMessages]);

  // Load thread history whenever we switch threads.
  //
  // `isLoading` is derived rather than set from the effect: switching threads
  // swaps `useChat`'s state during the same render that changes
  // `localThreadId`, so a flag that only flips once the effect runs leaves one
  // painted frame with no messages and nothing loading — long enough for the
  // empty "new chat" state to flash between two existing threads.
  const { loadThreadId } = options;
  const [loadedLocalThreadId, setLoadedLocalThreadId] = useState<string>();
  const isLoading =
    loadThreadId !== undefined && loadedLocalThreadId !== localThreadId;
  const [loadThreadMessages] = useLazyMastraThreadMessagesQuery();

  const onLoad = useEffectEvent(async (signal: AbortSignal) => {
    try {
      if (loadThreadId === undefined) {
        return;
      }

      // Not `.unwrap()`: it rejects an aborted request with a plain `Error` that
      // `checkIsAbortError` can't tell from a real failure, whereas the promise
      // itself resolves and lets the abort check below run first.
      const request = loadThreadMessages({ agentId, threadId: loadThreadId });
      signal.addEventListener("abort", () => request.abort());
      const { data, error } = await request;

      // `setMessages` always writes to whichever thread is mounted now, so a
      // superseded load would drop its history into the thread that replaced
      // it.
      if (signal.aborted) {
        return;
      }
      if (error || !data) {
        throw new Error(
          `Failed to load chat history: ${JSON.stringify(error)}`,
        );
      }

      // `toAISdkMessages` feeds a Mastra `MessageList`, which stamps `createdAt`
      // onto the messages it is handed. RTK Query's cache is frozen, so it has to
      // get a copy -- the pre-RTK fetch happened to pass freshly parsed JSON.
      setMessages(
        toAISdkMessages(structuredClone(data.messages), { version: "v6" }),
      );
    } finally {
      // A superseded load must leave `loadedLocalThreadId` alone: it is what
      // holds `isLoading` false, and the effect only runs once per thread, so
      // stamping the abandoned thread's ID over the current one's would strand
      // the skeleton forever, and an aborted load still unwinds through here
      // after its replacement has started.
      //
      // Threads with no history to load still get marked, so `isLoading` stays
      // false once such a thread gains a remote counterpart of its own.
      if (!signal.aborted) {
        setLoadedLocalThreadId(localThreadId);
      }
    }
  });

  useEffect(() => {
    const abortController = new AbortController();
    onLoad(abortController.signal).catch((error: unknown) => {
      if (checkIsAbortError(error)) {
        return;
      }
      throw error;
    });
    return () => abortController.abort();
  }, [agentId, localThreadId, onLoad]);

  return {
    messages,
    sendMessage: (text: string) => sendMessage({ text }),
    stop,
    isRunning: status === "submitted" || status === "streaming",
    isLoading,
    setMessages,
  };
}

function getLangfuseSessionId(options: {
  agentId: string;
  localThreadId: string | undefined;
  thread: { threadId: string } | undefined;
}) {
  if (options.thread) {
    return getMastraThreadLangfuseSessionId(
      options.agentId,
      options.thread.threadId,
    );
  }
  if (options.localThreadId) {
    return `${options.agentId}_client_thread_${options.localThreadId}`;
  }
  return undefined;
}
